import BackButton from "components/back-button/back-button.component";
import Input from "components/input/input.component";
import {
  BottomButtonsContainer,
  DisclaimerContainer,
  FormPartContainer,
  NextStepButton,
  SectionHeading,
  TwoColumnsInput,
} from "global";
import { emailRegex, phoneNumberRegex } from "utils/form-constants";
import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  updateSecuritiesInfo,
  updateFormErrors,
} from "redux/form/form.reducer";
import {
  selectFormErrors,
  selectSecuritiesInfo,
} from "redux/form/form.selector";
import { updateCurrentForm, updatePopup } from "redux/helpers/helpers.reducer";
import { selectCurrentForm, selectPopup } from "redux/helpers/helpers.selector";
import datalayerAPI from "utils/datalayerAPI";
import {
  nameRegex,
  numericAndLettersAndDashRegex,
  popupMessages,
} from "utils/form-constants";
import {
  scrollToTheError,
  scrollToTheTop,
  validate,
} from "utils/helper-functions";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { ValidationErrors } from "utils/interfaces";
import {
  gosSelfManagedInstitutionSchema,
  gosBrokerManagedInstitutionSchema,
} from "utils/validation-schemas";
import { ValidationError } from "yup";

const GosInstitutionInfo: FC = () => {
  const currentForm = useAppSelector(selectCurrentForm);
  const securitiesInfo = useAppSelector(selectSecuritiesInfo);
  const formErrors = useAppSelector(selectFormErrors);
  const popup = useAppSelector(selectPopup);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const lastTrackedValuesRef = useRef<Record<string, string>>({});

  const isSelfManaged =
    securitiesInfo.securitiesManagementType === "self-directed";

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    ignoreTracking?: boolean,
  ) => {
    const { name, value } = e.currentTarget;
    const isCurrentlyFocused = document.activeElement === e.currentTarget;

    switch (name) {
      case "securitiesAccountNumber":
        if (numericAndLettersAndDashRegex.test(value))
          dispatch(updateSecuritiesInfo({ ...securitiesInfo, [name]: value }));
        break;
      case "advisorFirstName":
      case "advisorLastName":
        if (nameRegex.test(value))
          dispatch(updateSecuritiesInfo({ ...securitiesInfo, [name]: value }));
        break;
      case "advisorEmail":
        if (!emailRegex.test(value)) return;
        dispatch(updateSecuritiesInfo({ ...securitiesInfo, [name]: value }));
        break;
      case "advisorPhoneNumber":
        if (!phoneNumberRegex.test(value)) return;
        dispatch(updateSecuritiesInfo({ ...securitiesInfo, [name]: value }));
        break;
      case "selfDirectedPlatform":
      case "brokerageName":
        dispatch(updateSecuritiesInfo({ ...securitiesInfo, [name]: value }));
        break;
      default:
        break;
    }

    // If field is NOT focused, this was browser autofill - fire event immediately
    if (
      !ignoreTracking &&
      !isCurrentlyFocused &&
      lastTrackedValuesRef.current[name] !== value
    ) {
      lastTrackedValuesRef.current[name] = value;
      datalayerAPI.handleFieldValueChange(name);
    }
  };

  const handleNextStep = async () => {
    try {
      // Validate
      await validate(
        {
          ...securitiesInfo,
        },
        isSelfManaged
          ? gosSelfManagedInstitutionSchema
          : gosBrokerManagedInstitutionSchema,
      );
      dispatch(updateCurrentForm(2));
      dispatch(updateFormErrors({}));

      // Scroll to the top
      scrollToTheTop();
    } catch (error: unknown) {
      const errors: ValidationErrors = {};

      (error as ValidationError).inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });

      scrollToTheError(Object.keys(errors)[0]);

      // Display popup
      dispatch(updateFormErrors(errors));
      dispatch(
        updatePopup({
          ...popup,
          message: popupMessages.errorsFixed,
          isError: true,
          isActive: true,
          isLoading: false,
        }),
      );
    }
  };

  const accountElement = (
    <Input
      label="Transfer securities from account"
      type="text"
      name="securitiesAccountNumber"
      id="securitiesAccountNumber"
      onChange={handleInputChange}
      value={securitiesInfo.securitiesAccountNumber}
      isError={formErrors.securitiesAccountNumber}
      maxLength={20}
      isRequired
    />
  );

  return (
    <FormPartContainer currentForm={currentForm} formID={1}>
      {isSelfManaged ? (
        <div>
          <SectionHeading>{t("Institution details")}</SectionHeading>

          <Input
            label="Self-directed platform"
            type="text"
            name="selfDirectedPlatform"
            id="selfDirectedPlatform"
            onChange={handleInputChange}
            value={securitiesInfo.selfDirectedPlatform}
            isError={formErrors.selfDirectedPlatform}
            maxLength={50}
            isRequired
          />

          {accountElement}

          <DisclaimerContainer style={{ marginBottom: 32 }}>
            <strong>{t("Please Note")}:</strong>{" "}
            {t("Account holder disclaimer")}
          </DisclaimerContainer>
        </div>
      ) : (
        <div>
          <SectionHeading>{t("Broker information")}</SectionHeading>
          <Input
            label="Brokerage / financial institution"
            type="text"
            name="brokerageName"
            id="brokerageName"
            onChange={handleInputChange}
            value={securitiesInfo.brokerageName}
            isError={formErrors.brokerageName}
            maxLength={50}
            isRequired
          />

          {accountElement}

          <SectionHeading>{t("Contact information")}</SectionHeading>

          <TwoColumnsInput>
            <Input
              label="Advisor First Name"
              type="text"
              name="advisorFirstName"
              id="advisorFirstName"
              onChange={handleInputChange}
              value={securitiesInfo.advisorFirstName}
              isError={formErrors.advisorFirstName}
              maxLength={50}
              isRequired
            />
            <Input
              label="Advisor Last Name"
              type="text"
              name="advisorLastName"
              id="advisorLastName"
              onChange={handleInputChange}
              value={securitiesInfo.advisorLastName}
              isError={formErrors.advisorLastName}
              maxLength={50}
              isRequired
            />
          </TwoColumnsInput>
          <TwoColumnsInput>
            <Input
              label="Advisor phone number"
              type="text"
              name="advisorPhoneNumber"
              id="advisorPhoneNumber"
              onChange={handleInputChange}
              value={securitiesInfo.advisorPhoneNumber}
              isError={formErrors.advisorPhoneNumber}
              maxLength={50}
            />
            <Input
              label="Advisor email"
              type="text"
              name="advisorEmail"
              id="advisorEmail"
              onChange={handleInputChange}
              value={securitiesInfo.advisorEmail}
              isError={formErrors.advisorEmail}
              maxLength={50}
              isRequired
            />
          </TwoColumnsInput>
        </div>
      )}

      <BottomButtonsContainer>
        <BackButton />

        <NextStepButton onClick={handleNextStep}>
          {t("Next Step")}
        </NextStepButton>
      </BottomButtonsContainer>
    </FormPartContainer>
  );
};

export default GosInstitutionInfo;
